import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { debounce } from '@mui/material/utils';
import parse from 'autosuggest-highlight/parse';
import {
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import getEnv from '../../env/enviroment';

const GOOGLE_MAPS_API_KEY = getEnv().google_maps_api_key;

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
  place_id: string;
}

type Props = {
  onPlaceSelect: (
    value: any,
    address: Array<{ [key: string]: unknown }>,
  ) => unknown;
  onPlaceError: (error: string) => unknown;
  placeHolder?: string;
  label: string;
  name: string;
  required?: boolean;
  inputText?: string;
  disabled?: boolean;
  testId?: string;
  inputVariant?: string;
};

const GooglePlaceInput = ({
  onPlaceSelect,
  onPlaceError,
  placeHolder,
  label,
  name,
  required,
  inputText,
  disabled,
  testId,
  inputVariant,
}: Props) => {
  const [value, setValue] = useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = useState(inputText || '');
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const loaded = useRef(false);

  useEffect(() => {
    if (typeof inputText !== undefined) {
      setInputValue(inputText as string);
    }
  }, [inputText]);

  const searchOptions = {
    componentRestrictions: { country: 'us' },
  };

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps',
      );
    }
    loaded.current = true;
  }

  const fetchPlaces = useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            { ...request, ...searchOptions },
            callback,
          );
        },
        400,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetchPlaces({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inputValue, fetch]);

  const onSelection = (
    placeId: string | undefined,
    description: string | undefined,
  ) => {
    if (placeId) {
      const geocoder = new (window as any).google.maps.Geocoder();
      geocoder.geocode(
        { placeId },
        function (results: Array<{ [key: string]: unknown }>, status: any) {
          if (status === 'OK') {
            onPlaceSelect(description, results);
          } else {
            onPlaceError(status);
          }
        },
      );
    }
  };
  return (
    <Autocomplete
      forcePopupIcon={false}
      id={testId}
      sx={{ width: '100%' }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      disabled={disabled}
      noOptionsText="Address Not Found"
      onChange={(event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        onSelection(newValue?.place_id, newValue?.description);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          inputProps={{
            ...params.inputProps,
            value: inputValue,
            variant: inputVariant || 'outlined',
            'data-testid': testId,
          }}
          name={name}
          placeholder={placeHolder}
          required={required}
          fullWidth
        />
      )}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];
        const parts = parse(
          option.description,
          matches.map((match: any) => [
            match.offset,
            match.offset + match.length,
          ]),
        );
        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{ width: '100%', wordWrap: 'break-word' }}>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                  >
                    {part.text}
                  </Box>
                ))}
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};

export default GooglePlaceInput;
